import { EventContentArg } from '@fullcalendar/core';
import esLocale from '@fullcalendar/core/locales/es';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import resourceDayGridPlugin from '@fullcalendar/resource-daygrid';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { isEmpty } from 'lodash';
import React from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { useWorkspaceContext } from '../../../../providers/WorkspaceProvider/Workspace.context';
import { listViewTimeFormat } from './Calendar.constants';
import { eCalendarView } from './Calendar.enums';
import { slotLabelContent } from './Calendar.functions';
import { useCalendarContext } from './Calendar.hook';
import style from './Calendar.module.scss';
import CalendarSkeleton from './components/CalendarSkeleton.component';
import { EventContent } from './components/EventContent.component';
import HeaderToolbar from './components/HeaderToolbar.component';
import NoEventsOrTemplate from './components/NoEvents.component';
import SubHeader from './components/SubHeader.component';

export default function Calendar(): React.ReactElement {
  const { workspaceInfo } = useWorkspaceContext();
  const {
    events,
    onEventClick,
    calendarRef,
    selectedEvent,
    templates,
    currentView,
    onDatesSetUpdate,
    initialLoading,
    hasFetchedEvents,
  } = useCalendarContext();

  const resources = Object.values(workspaceInfo?.users ?? {}).map((user) => ({
    id: user.authId,
    title: user.name,
  }));

  const shouldShowDateHeader =
    currentView === eCalendarView.LIST_DAY ? false : listViewTimeFormat;

  return (
    <div>
      {initialLoading ? (
        <div className={style.Calendar + ' mt-4'}>
          <CalendarSkeleton />
        </div>
      ) : hasFetchedEvents && isEmpty(templates) && isEmpty(events) ? (
        <div className={style.Calendar + ' mt-4'}>
          <NoEventsOrTemplate
            hasEvents={events.length}
            hasTemplates={templates.length}
          />
        </div>
      ) : (
        <>
          <HeaderToolbar />
          <div className={style.Calendar}>
            <SubHeader />
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                interactionPlugin,
                resourceTimeGridPlugin,
                resourceTimelinePlugin,
                resourceDayGridPlugin,
                listPlugin,
              ]}
              headerToolbar={false}
              schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
              initialView="listDay"
              listDayFormat={shouldShowDateHeader}
              listDaySideFormat={false}
              editable={true}
              selectable={true}
              selectMirror={true}
              weekends={true}
              resourceOrder="id"
              resources={resources}
              eventContent={(arg: EventContentArg) => (
                <EventContent
                  arg={arg}
                  workspaceUsers={Object.values(workspaceInfo?.users ?? {})}
                  selectedEvent={selectedEvent}
                />
              )}
              select={onEventClick}
              // eventsSet={onEventClick}
              // dateClick={onEventClick}
              datesSet={onDatesSetUpdate}
              locale={esLocale}
              events={events}
              slotLabelContent={slotLabelContent} // Custom function to render time slot labels
              nowIndicator // Show current time indicator
              slotDuration="00:30:00" // Each time slot represents one hour
              slotMinTime="07:00:00" // Set the start time for time slots
              slotMaxTime="23:00:00" // Set the end time for time slots
              slotLabelInterval="00:30:00"
              allDayText=""
              selectAllow={(selectInfo) => {
                const today = new Date();

                return selectInfo.start >= today; // only allow selecting dates from today onwards
              }}
              height="auto"
            />
          </div>
        </>
      )}
    </div>
  );
}
